import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { ProjectCard } from './ProjectCard';
import { Button } from '../../../core/components/Button';
import { useUnits } from '../../../core/api';
import { RemoteImage } from '../../../core/components/RemoteImage';

export interface FeaturedUnitsConfig {
    itemIds?: string[];
    title?: { en: string; ar: string } | string;
    description?: { en: string; ar: string } | string;
    backgroundImage?: string;
    useCustomBackground?: boolean;
    ctaUrl?: string;
}

interface FeaturedUnitsSliderProps {
    config?: FeaturedUnitsConfig;
}

export const FeaturedUnitsSlider = ({ config }: FeaturedUnitsSliderProps) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';
    const lang = isRTL ? 'ar' : 'en';
    const scrollRef = useRef<HTMLDivElement>(null);

    // Determine effective config
    const itemIds = config?.itemIds || [];
    const hasIds = itemIds.length > 0;

    const rawTitle = config?.title;
    const title = typeof rawTitle === 'object' ? rawTitle[lang] : rawTitle;

    const rawDesc = config?.description;
    const description = typeof rawDesc === 'object' ? rawDesc[lang] : rawDesc;

    // Fetch featured/published units from the API
    const { data: unitsResponse, isLoading } = useUnits({
        limit: 10,
        ids: hasIds ? itemIds : undefined
    });
    const units = (unitsResponse as any) || [];

    const featuredUnits = units.map((unit: any) => ({
        id: unit.id,
        title: unit.title || unit.slug,
        location: unit.project?.title || '',
        specs: {
            beds: unit.beds ?? 0,
            baths: unit.baths ?? 0,
            area: `${unit.area ?? 0} ${t('common.m2', 'm²')}`
        },
        price: unit.price,
        payment: unit.payment || t('common.installments', 'Installments'),
        status: unit.status || '',
        image: unit.image || '',
        constructionStatus: unit.constructionStatus || undefined,
        finishType: unit.finishType || undefined,
        deliveryDate: unit.deliveryDate || undefined,
    }));

    const scroll = (direction: 'left' | 'right') => {
        if (scrollRef.current) {
            const cardWidth = 320 + 24;
            const actualDirection = isRTL ? (direction === 'left' ? 1 : -1) : (direction === 'left' ? -1 : 1);
            scrollRef.current.scrollBy({ left: cardWidth * actualDirection, behavior: 'smooth' });
        }
    };

    if (!isLoading && featuredUnits.length === 0) return null;

    // Background Image Logic
    const isCustomBgEnabled = config?.useCustomBackground === true || String(config?.useCustomBackground) === 'true';
    const backgroundImage = (isCustomBgEnabled && config?.backgroundImage)
        ? config.backgroundImage
        : (units[0]?.image || '');

    return (
        <section className="relative bg-transparent flex flex-col pt-12 sm:pt-24">
            {/* 1. Banner Section (Top Half) */}
            <div className="relative h-[400px] sm:h-[450px] md:h-[500px] w-full bg-slate-900 overflow-hidden">
                {/* Background Image with Overlay */}
                <div className="absolute inset-0 z-0">
                    {isLoading ? (
                        <div className="absolute inset-0 bg-slate-800 animate-pulse" />
                    ) : (
                        <RemoteImage
                            src={backgroundImage}
                            alt="Background"
                            className="w-full h-full object-cover opacity-50 grayscale"
                        />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-slate-900/60" />
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 h-full flex flex-col pt-12 sm:pt-16 pointer-events-none">
                    {/* Header Content */}
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 h-full max-h-[60%] pointer-events-auto">
                        <div className="text-white max-w-2xl">
                            <h2 className={clsx(
                                "text-3xl sm:text-4xl md:text-5xl font-display font-bold mb-4 uppercase text-white drop-shadow-lg",
                                isRTL && "font-sans"
                            )}>
                                {title || t('featuredUnits.title', 'FEATURED UNITS')}
                            </h2>
                            <p className="text-slate-100 text-sm sm:text-base max-w-xl font-light drop-shadow-md mb-6">
                                {description || t('featuredUnits.subtitle', 'Explore our exclusive selection of premium properties designed for your lifestyle.')}
                            </p>

                            {/* View Details Button */}
                            <Link to={config?.ctaUrl || "/explore"}>
                                <Button
                                    variant="primary"
                                    className="bg-white text-secondary hover:bg-slate-100 border-none shadow-xl"
                                >
                                    {t('featured.viewDetails', 'View Details')}
                                </Button>
                            </Link>
                        </div>

                        {/* Navigation Arrows */}
                        <div className="hidden md:flex items-center gap-4">
                            <button
                                onClick={() => scroll('left')}
                                className="w-12 h-12 rounded-full bg-white text-secondary hover:bg-slate-200 transition-colors flex items-center justify-center shadow-lg"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M9 5l7 7-7 7" : "M15 19l-7-7 7-7"} />
                                </svg>
                            </button>
                            <button
                                onClick={() => scroll('right')}
                                className="w-12 h-12 rounded-full bg-white text-secondary hover:bg-slate-200 transition-colors flex items-center justify-center shadow-lg"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* 2. Overlapping Cards Section (Bottom Half) */}
            <div className="relative z-20 -mt-32 sm:-mt-40 md:-mt-48 max-w-7xl mx-auto w-full px-4 sm:px-6 mb-16">
                {/* Mobile Navigation Arrows */}
                <div className="flex md:hidden justify-end gap-3 mb-4">
                    <button
                        onClick={() => scroll('left')}
                        className="w-10 h-10 rounded-full bg-white text-secondary shadow-md flex items-center justify-center"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M9 5l7 7-7 7" : "M15 19l-7-7 7-7"} />
                        </svg>
                    </button>
                    <button
                        onClick={() => scroll('right')}
                        className="w-10 h-10 rounded-full bg-white text-secondary shadow-md flex items-center justify-center"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
                        </svg>
                    </button>
                </div>

                {/* Loading skeletons */}
                {isLoading && (
                    <div className="flex gap-6 pb-12">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="min-w-[280px] sm:min-w-[340px] h-[360px] rounded-xl bg-slate-200 animate-pulse" />
                        ))}
                    </div>
                )}

                {!isLoading && featuredUnits.length > 0 && (
                    <div
                        ref={scrollRef}
                        className="flex overflow-x-auto gap-6 pb-12 snap-x snap-mandatory scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                    >
                        {featuredUnits.map((unit: any, index: number) => (
                            <motion.div
                                key={unit.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <ProjectCard
                                    project={unit}
                                    linkTo={`/units/${units[index]?.slug || unit.id}`}
                                    className="min-w-[280px] sm:min-w-[340px] snap-center"
                                    type="Unit"
                                />
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};
